/**
 * PluginManager - plugin lifecycle management. Handles loading, unloading,
 * compilation, and state persistence for runtime plugin code.
 */

import { ObjectTracker } from '../systems/ObjectTracker.js';
import { UserCodeEngine } from '../utils/UserCodeEngine.js';

import type { IPlugin } from './Plugin.js';
import type { World } from '../game/World.js';

/**
 * Manages the loading, unloading, and lifecycle of plugins in the Genesys engine.
 *
 * The PluginManager provides dynamic code execution capabilities, allowing plugins
 * to be loaded from TypeScript/JavaScript source code at runtime. It handles:
 * - Compilation of plugin source code
 * - Plugin instantiation and initialization
 * - State persistence across plugin reloads
 * - Safe disposal of plugin resources
 */
export class PluginManager {
  private userCodeEngine: UserCodeEngine = new UserCodeEngine();
  private world: WeakRef<World>;
  private plugins: Record<string, IPlugin> = {};
  private pluginStates: Record<string, any> = {};

  /**
 * Creates a new PluginManager instance.
 *
 * @param world - WeakRef to the game world to prevent circular references
 */
  constructor(world: WeakRef<World>) {
    ObjectTracker.track(this);
    this.world = world;
    this.userCodeEngine.initialize();
  }

  /**
   * Load and initialize a plugin from TypeScript/JavaScript source code.
   *
   * @param name - Unique identifier for the plugin
   * @param code - TypeScript/JavaScript source code implementing IPlugin interface
   * @param loadPreviousState - Whether to restore the plugin's previous state
   * @returns The loaded plugin instance, or null if loading failed
   */
  public async loadPlugin(name: string, code: string, loadPreviousState: boolean): Promise<IPlugin | null> {
    this.unloadPlugin(name);

    const result = await this.userCodeEngine.compile(this.wrapInSandbox(code));
    if (result.errors.length > 0) {
      console.error('Compilation errors:', result.errors);
      return null;
    }

    const module = await this.userCodeEngine.import(result);
    const pluginClass = module.default;
    if (!pluginClass) {
      console.error('Plugin class not found');
      return null;
    }

    try {
      const plugin = new pluginClass();
      if (await plugin.init(this.world.deref(), loadPreviousState ? this.pluginStates[name] : undefined)) {
        this.plugins[name] = plugin;
        return plugin;
      } else {
        console.error(`Failed to initialize ${name} plugin.`);
        return null;
      }
    } catch (error) {
      console.error('Error initializing plugin:', error);
      return null;
    }
  }

  /**
   * Unload a plugin, saving its state and cleaning up resources.
   *
   * @param name - Name of the plugin to unload
   */
  public async unloadPlugin(name: string): Promise<void> {
    this.pluginStates[name] = this.plugins[name]?.saveState();
    this.plugins[name]?.dispose();
    delete this.plugins[name];
  }

  private wrapInSandbox(code: string): string {
    return `
            "use strict";

            // Disable dangerous globals
            const window = undefined;
            const document = undefined;
            // const eval = undefined;
            const Function = undefined;
            const require = undefined;
            const process = undefined;
            const global = undefined;
            const globalThis = undefined;

            ${code}
        `;
  }
}
